import { AppRoutes } from "@fero/utils";
import type { PurchaseRequestDeliveryOption } from "adl-gen/ferovinum/app/db";
import { assertNever } from "assert-never";
import { useCallback, useMemo } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { formatDateToISO8601, formatDateToLocalTime } from "utils/date-utils";
import { assertNotUndefined } from "utils/hx/util/types";
import type { PurchaseRequestFlowState } from "../purchase-request-types";
import {
  type DeliveryPurchaserRequestFormValues,
  OrganisationPurchaseRequestDeliveryOptionsPageView,
  PaymentResponsibilityOption,
} from "./organisation-purchase-request-delivery-options-page-view";

export const OrganisationPurchaseRequestDeliveryOptionsPage = () => {
  const location = useLocation();
  const history = useHistory();

  const purchaseRequestFlowState = location.state as
    | PurchaseRequestFlowState
    | undefined;

  const deliveryIsAvailable = useMemo(() => {
    const setupFlowState = assertNotUndefined(purchaseRequestFlowState?.setup);
    return assertNotUndefined(setupFlowState.storageLocation)
      .availableForShipping;
  }, [purchaseRequestFlowState?.setup]);

  const [storageLocation] = useMemo(() => {
    const setupFlowState = assertNotUndefined(purchaseRequestFlowState?.setup);
    return [assertNotUndefined(setupFlowState.storageLocation)];
  }, [purchaseRequestFlowState?.setup]);

  const isBondedSale = useMemo(() => {
    const setupFlowState = assertNotUndefined(purchaseRequestFlowState?.setup);
    return assertNotUndefined(setupFlowState.bondedSale);
  }, [purchaseRequestFlowState?.setup]);
  const onNext = async (values: DeliveryPurchaserRequestFormValues) => {
    const newState: PurchaseRequestFlowState = {
      ...purchaseRequestFlowState,
      delivery: {
        preparationDaysType: assertNotUndefined(values.preparationDaysType),
        preparationDays: assertNotUndefined(values.preparationDays),
        deliveryOption: toDeliveryOption(
          values,
          purchaseRequestFlowState?.setup?.bondedSale ?? false,
        ),
        purchaserCoversDeliveryCost:
          values.deliveryFeePaidBy === PaymentResponsibilityOption.Purchaser ||
          values.deliveryFeePaidBy ===
            PaymentResponsibilityOption.PurchaserCustom,
        customerPoRef: values.customerPoRef,
        shipmentNumber: values.shipmentNumber,
        deliveryInstructionDocs: values.deliveryInstructionDocs,
        customDeliveryCost: values.customDeliveryCost
          ? {
              amount: String(values.customDeliveryCost),
              vat: null,
              description: "custom delivery cost",
            }
          : undefined,
        additionalDeliveryCost: values.additionalDeliveryCost?.amount
          ? values.additionalDeliveryCost
          : undefined,
      },
    };
    history.push(AppRoutes.OrganisationPurchaseRequestSettlement, newState);
  };

  const onBack = useCallback(() => {
    if (purchaseRequestFlowState?.productionPlan !== undefined) {
      history.push(
        AppRoutes.OrganisationPurchaseRequestProductionPlan,
        purchaseRequestFlowState,
      );
    } else {
      history.push(
        AppRoutes.OrganisationCreatePurchaseRequest,
        purchaseRequestFlowState,
      );
    }
  }, [history, purchaseRequestFlowState]);
  if (purchaseRequestFlowState?.setup === undefined) {
    return <Redirect to={AppRoutes.OrganisationPurchaseRequests} />;
  }
  return (
    <OrganisationPurchaseRequestDeliveryOptionsPageView
      purchaser={purchaseRequestFlowState.setup.purchaser}
      initialValues={purchaseRequestFlowState.delivery}
      deliveryIsAvailable={deliveryIsAvailable}
      hasProductionPlanAttached={
        purchaseRequestFlowState?.productionPlan !== undefined
      }
      storageLocation={storageLocation}
      isBondedSale={isBondedSale}
      products={purchaseRequestFlowState.setup.products}
      onNext={onNext}
      onBack={onBack}
    />
  );
};

function toDeliveryOption(
  values: DeliveryPurchaserRequestFormValues,
  bondedSale: boolean,
): PurchaseRequestDeliveryOption {
  switch (values.deliveryType) {
    case "collectFromStorageLocation":
      return {
        kind: "collectFromStorageLocation",
        value: {
          nominatedPurchaserCollection: { bonded: bondedSale },
          incoterms: assertNotUndefined(values.collectionIncoterms),
          purchaserCollectionDetails: { kind: "notAvailable" },
        },
      };
    case "deliveryToNominatedPurchaser":
      return {
        kind: "deliveryToNominatedPurchaser",
        value: {
          deliveryDetails: {
            ...values.deliveryDetails,
            bonded: bondedSale,
            // Need to put the dates into the right format for the API
            deliveryDate: values.deliveryDetails.deliveryDate
              ? formatDateToISO8601(values.deliveryDetails.deliveryDate)
              : null,
            deliveryTimeEarliest: values.deliveryDetails.deliveryTimeEarliest
              ? formatDateToLocalTime(
                  values.deliveryDetails.deliveryTimeEarliest,
                )
              : null,
            deliveryTimeLatest: values.deliveryDetails.deliveryTimeLatest
              ? formatDateToLocalTime(values.deliveryDetails.deliveryTimeLatest)
              : null,
            deliveryContactEmail: values.deliveryDetails.deliveryContactEmail
              ? values.deliveryDetails.deliveryContactEmail.toLowerCase()
              : null,
          },
        },
      };
    default:
      assertNever(values.deliveryType);
  }
}
